import { useTranslation } from 'react-i18next'
import { useBreakpoint } from '~/hooks'
import { useSpotSwapPair } from '~/state/dex/hooks'
import { useCrossTokens } from '~/components/Exchange/hooks'
import TokenLogo from '~/components/TokenLogo'
import NewTag from '../NewTag'
import { FlexContainer } from '~/components/Layout'
import { getNetworkLogo } from '~/utils/chain'
import styled from 'styled-components'

interface CrossTokensProps {
  selectedChainId: number | null
  onSelect: (token: SpotSwapToken) => void
}

interface TokenItemProps {
  logo: string
  symbol: string
  networkLogo: string
  showNetworkBadge: boolean
  onClick: () => void
}

const TokenItem: React.FC<TokenItemProps> = ({
  logo,
  symbol,
  networkLogo,
  showNetworkBadge,
  onClick,
}) => {
  return (
    <StyledToken onClick={onClick}>
      <TokenLogo
        large
        logo={logo}
        symbol={symbol}
        space={0}
        showNetworkBadge={showNetworkBadge}
        networkLogo={networkLogo}
      />
      <StyledSymbolText>{symbol}</StyledSymbolText>
    </StyledToken>
  )
}

const CrossTokens: React.FC<CrossTokensProps> = ({
  selectedChainId,
  onSelect,
}) => {
  const { t } = useTranslation()
  const { isMobile } = useBreakpoint()
  const [inputToken] = useSpotSwapPair()
  const crossTokens = useCrossTokens(selectedChainId)

  if (crossTokens.length === 0) return null

  const isSelectAll = selectedChainId === null
  const isSelectInputNetwork = selectedChainId === inputToken?.chainId
  const isShowNetworkBadge = isSelectAll || isSelectInputNetwork
  const gap = isMobile ? 0 : 14

  return (
    <Container>
      <LabelWrapper>
        <StyledLabel>{t('tokenlist_cross_tokens_label')}</StyledLabel>
        <NewTag />
      </LabelWrapper>

      <TokensContainer wrap="wrap" align="center" gap={gap}>
        {crossTokens.map((token) => {
          const { chainId, symbol, address, logo } = token
          const networkLogo = getNetworkLogo(chainId)
          return (
            <TokenItem
              onClick={() => onSelect(token)}
              key={`${chainId}-${address}`}
              symbol={symbol}
              logo={logo}
              networkLogo={networkLogo}
              showNetworkBadge={isShowNetworkBadge}
            />
          )
        })}
      </TokensContainer>
    </Container>
  )
}

export default CrossTokens

const Container = styled.div`
  padding: 0 20px 10px 20px;
`

const LabelWrapper = styled.div`
  display: flex;
  align-items: center;
`

const StyledLabel = styled.p`
  margin: 0;
  margin-right: 7px;
  font-size: 14px;
  font-weight: 400;
  color: #b9bed3;
`

const TokensContainer = styled(FlexContainer)`
  margin-top: 12px;
  margin-left: -8px;
`

const StyledToken = styled.div`
  border-radius: 8px;
  padding: 8px;
  cursor: pointer;

  &:hover {
    background: #f5f7fb;
  }
`

const StyledSymbolText = styled.span`
  display: inline-block;
  width: 100%;
  margin-top: 4px;
  font-size: 12px;
  font-weight: 400;
  color: #000000;
  text-align: center;
`
